import { GridContent } from '@ant-design/pro-components';
import { useRequest, history } from '@umijs/max';
import { Card, Tag, Button, Typography, Space, Spin, Empty, Alert, Segmented, theme } from 'antd';
import { CrownOutlined, CheckCircleFilled, InfoCircleFilled, CalendarOutlined } from '@ant-design/icons';
import React from 'react';
import { getPlans } from '@/services/payment';
import { createStyles } from 'antd-style';
import { normalizePlanList, flattenPlanTabs, normalizeCycleKey, type CurrentSubscriptionSummary } from '@/pages/payment/utils';

const { Title, Text } = Typography;

const useStyles = createStyles(({ token }) => ({
  hero: {
    padding: '28px 0 18px 0',
    marginBottom: 18,
    borderRadius: token.borderRadiusLG,
    background: `radial-gradient(1200px 240px at 50% -30%, ${token.colorPrimaryBgHover} 0%, transparent 60%),
      linear-gradient(180deg, ${token.colorBgContainer} 0%, ${token.colorBgLayout} 100%)`,
    border: `1px solid ${token.colorBorderSecondary}`,
  },
  currentSubscriptionSummary: {
    marginTop: 14,
    marginInline: 'auto',
    maxWidth: 820,
    padding: '16px 20px',
    borderRadius: 16,
    background: `linear-gradient(135deg, ${token.colorPrimaryBg} 0%, ${token.colorInfoBg} 100%)`,
    border: `1px solid ${token.colorPrimaryBorder}`,
    boxShadow: '0 16px 30px -24px rgba(0,0,0,0.35)',
  },
  currentSubscriptionSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 8,
    color: token.colorPrimary,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: '0.3px',
  },
  currentSubscriptionSummaryTitle: {
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 900,
    color: token.colorTextHeading,
    lineHeight: 1.3,
  },
  currentSubscriptionSummaryDesc: {
    marginTop: 8,
    textAlign: 'center',
    fontSize: 14,
    color: token.colorTextSecondary,
  },
  tabToggleWrap: {
    display: 'flex',
    justifyContent: 'center',
    margin: '18px 0 18px 0',
  },
  tabToggle: {
    padding: 6,
    borderRadius: 999,
    backgroundColor: token.colorFillQuaternary,
    border: `1px solid ${token.colorBorderSecondary}`,
  },
  plansGrid: {
    display: 'grid',
    gap: 18,
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 320px))',
    justifyContent: 'center',
    alignItems: 'stretch',
    padding: '8px 0 18px 0',
    ['@media (max-width: 640px)']: {
      gridTemplateColumns: '1fr',
      justifyContent: 'stretch',
    },
  },
  planCard: {
    position: 'relative',
    borderRadius: 20,
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    border: `1px solid ${token.colorBorderSecondary}`,
    backgroundColor: token.colorBgContainer,
    boxShadow: '0 10px 28px -18px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 18px 42px -24px rgba(0,0,0,0.35)',
    },
  },
  selectedCard: {
    borderColor: token.colorPrimary,
    boxShadow: `0 18px 48px -26px ${token.colorPrimaryBorder}`,
  },
  selectedBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    background: token.colorPrimaryBg,
    color: token.colorPrimaryTextActive,
    border: `1px solid ${token.colorPrimaryBorder}`,
  },
  currentPlanBadge: {
    background: token.colorPrimary,
    color: '#fff',
    borderRadius: '0 0 0 16px',
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 700,
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  planHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    minHeight: 56,
    marginBottom: 4,
  },
  priceContainer: {
    textAlign: 'center',
    padding: '20px 0 18px 0',
    background: token.colorFillQuaternary,
    borderRadius: 16,
    margin: '14px 0 18px 0',
    border: `1px solid ${token.colorBorderSecondary}`,
  },
  price: {
    fontSize: 38,
    fontWeight: 800,
    color: token.colorTextHeading,
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
  currency: {
    fontSize: 20,
    marginRight: 4,
    fontWeight: 600,
  },
  cycle: {
    fontSize: 16,
    color: token.colorTextSecondary,
    marginLeft: 4,
    fontWeight: 400,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 14,
    fontSize: 15,
  },
  checkIcon: {
    color: token.colorSuccess,
    fontSize: 18,
    marginTop: 2,
  },
  subscribeButton: {
    height: 52,
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 800,
    marginTop: 10,
    border: 'none',
    boxShadow: '0 10px 20px -12px rgba(0,0,0,0.35)',
    transition: 'all 0.3s',
    '&:not(:disabled):hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 14px 26px -14px rgba(0,0,0,0.45)',
    },
  },
  stickyBar: {
    position: 'sticky',
    bottom: 12,
    zIndex: 10,
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center',
  },
  stickyBarInner: {
    maxWidth: 920,
    width: '100%',
    borderRadius: 16,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgElevated,
    boxShadow: '0 16px 46px -28px rgba(0,0,0,0.55)',
    padding: '12px 14px',
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    backdropFilter: 'saturate(140%) blur(10px)',
  },
  stickyTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: token.colorText,
  },
  stickyMeta: {
    fontSize: 12,
    color: token.colorTextSecondary,
  },
}));

type Plan = ReturnType<typeof flattenPlanTabs>[number];

const getBillingCycleLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'MONTHLY') return '月';
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return '年';
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return '两周';
  if (key === 'QUARTERLY') return '季';
  return billingCycle || '周期';
};

const getBillingCycleTabLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'MONTHLY') return '月付';
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return '年付';
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return '特惠';
  if (key === 'QUARTERLY') return '季付';
  return billingCycle || '套餐';
};

const formatPriceValue = (price: number | string) => {
  const numericPrice = Number(price || 0);
  if (!Number.isFinite(numericPrice)) {
    return '0';
  }
  if (numericPrice <= 0) {
    return '免费';
  }
  return numericPrice.toFixed(numericPrice % 1 === 0 ? 0 : 2);
};

const formatQuota = (quota: number | string) => {
  const n = Number(quota || 0);
  return (Number.isFinite(n) ? n : 0).toFixed(0);
};

const getRolloverText = (plan: Plan) => {
  if (plan.rolloverPolicy === 'FULL') {
    return '额度可全部结转';
  }
  if (plan.rolloverPolicy === 'RATIO' && plan.rolloverRatio != null) {
    return `可结转 ${Math.round(Number(plan.rolloverRatio) * 100)}% 额度`;
  }
  return '额度不结转';
};

const getPlanSubtitle = (plan: Plan) => {
  const custom = String(plan.displaySubtitle || '').trim();
  if (custom) return custom;

  const code = String(plan.planCode || '').toLowerCase();
  if (code === 'free') return '适合初次体验';
  if (code === 'special_offer') return '适合短期试用与快速上手';
  if (code.includes('basic')) return '适合轻量日常使用';
  if (code.includes('standard') || code === 'pro') return '适合稳定高频使用';
  if (code.includes('enterprise')) return '适合团队与更高配额场景';
  return '按当前使用规模选择';
};

const getDisplayBadge = (plan: Plan) => {
  const custom = String(plan.displayBadge || '').trim();
  if (custom) return custom;
  const cycle = normalizeCycleKey(plan.billingCycle);
  if (cycle === 'TWO_WEEK') return '限时体验';
  if (cycle === 'YEARLY') return '长期使用更划算';
  return '';
};

const getPlanFeatures = (plan: Plan) => {
  if (Array.isArray(plan.displayFeatures) && plan.displayFeatures.length > 0) {
    return plan.displayFeatures.filter(Boolean).slice(0, 4);
  }

  const features = [
    `包含 ${formatQuota(plan.creditQuota)} 点数`,
    `套餐周期 ${Number(plan.durationDays || 0) || 0} 天`,
    getRolloverText(plan),
  ];
  return features.filter(Boolean).slice(0, 4);
};

const formatSubscriptionExpiry = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const buildSubscriptionSummary = (currentSub?: CurrentSubscriptionSummary) => {
  const daysRemaining = Number(currentSub?.daysRemaining);
  const hasSubscription = currentSub?.hasSubscription === true
    || Boolean(currentSub?.planName)
    || Boolean(currentSub?.planCode)
    || Boolean(currentSub?.currentPeriodEnd)
    || Number.isFinite(daysRemaining);

  if (!hasSubscription) {
    return null;
  }

  const planName = String(currentSub.planName || currentSub.planCode || '当前套餐');
  const expiryText = formatSubscriptionExpiry(currentSub.currentPeriodEnd);
  const remainingText = Number.isFinite(daysRemaining) ? `（剩余 ${Math.max(0, daysRemaining)} 天）` : '';

  return {
    title: planName,
    description: expiryText ? `有效订阅至 ${expiryText}${remainingText}` : (remainingText ? `剩余 ${Math.max(0, daysRemaining)} 天` : ''),
  };
};

type StartCheckoutContext =
  | { orderType: 'SUBSCRIPTION'; planCode: string; planName?: string; billingCycle: string; durationDays?: number }
  | { orderType: 'RECHARGE'; rechargeAmount: number; currency?: string; paymentMethod?: string };

const Plans: React.FC<{
  embedded?: boolean;
  onStartCheckout?: (ctx: StartCheckoutContext) => void;
}> = ({ embedded, onStartCheckout }) => {
  const { styles } = useStyles();
  const { token } = theme.useToken();

  const {
    data: planList,
    loading,
    error,
  } = useRequest(getPlans, {
    // 套餐页只消费后端返回的展示/购买字段，不再自行推导订阅规则。
    formatResult: normalizePlanList,
    cacheKey: 'payment-plans',
    staleTime: 15_000,
    onError: (e) => {
      console.error('Failed to load plans:', e);
    },
  });
  const tabs = React.useMemo(() => Array.isArray(planList?.tabs) ? planList.tabs : [], [planList?.tabs]);
  const plans = React.useMemo(() => flattenPlanTabs(tabs), [tabs]);
  const currentSub = React.useMemo(
    () => planList?.currentSubscription,
    [planList?.currentSubscription],
  );
  const currentSubscriptionSummary = React.useMemo(
    () => buildSubscriptionSummary(currentSub),
    [currentSub],
  );
  const [activeTabKey, setActiveTabKey] = React.useState<string>('');

  React.useEffect(() => {
    if (tabs.length === 0) return;
    if (tabs.some((tab) => tab.tabKey === activeTabKey)) return;
    const defaultTabKey = String(planList?.defaultTabKey || '');
    const matched = tabs.find((tab) => tab.tabKey === defaultTabKey);
    setActiveTabKey(matched?.tabKey || tabs[0].tabKey);
  }, [tabs, activeTabKey, planList?.defaultTabKey]);

  const [selectedPlanCode, setSelectedPlanCode] = React.useState<string>('');
  const activeTab = React.useMemo(
    () => tabs.find((tab) => tab.tabKey === activeTabKey) || tabs[0],
    [tabs, activeTabKey],
  );
  const cyclePlans = React.useMemo(() => activeTab?.plans || [], [activeTab]);

  React.useEffect(() => {
    const exists = cyclePlans.some(
      (p) => String(p?.planCode || '').toLowerCase() === String(selectedPlanCode || '').toLowerCase(),
    );
    if (!exists && selectedPlanCode) {
      setSelectedPlanCode('');
    }
  }, [selectedPlanCode, cyclePlans]);

  const selectedPlan = React.useMemo(() => {
    const code = String(selectedPlanCode || '');
    return cyclePlans.find((p) => String(p?.planCode || '').toLowerCase() === code.toLowerCase());
  }, [cyclePlans, selectedPlanCode]);

  const selectedPlanIsFree = React.useMemo(() => {
    const n = Number(selectedPlan?.price ?? 0);
    return !Number.isFinite(n) || n <= 0;
  }, [selectedPlan?.price]);

  const handleSubscribe = (plan: Plan) => {
    if (embedded && onStartCheckout) {
      onStartCheckout({
        orderType: 'SUBSCRIPTION',
        planCode: String(plan.planCode || ''),
        planName: String(plan.planName || ''),
        billingCycle: String(plan.billingCycle || ''),
        durationDays: Number(plan.durationDays || 0) || undefined,
      });
      return;
    }

    const params = new URLSearchParams();
    params.set('orderType', 'SUBSCRIPTION');
    params.set('planCode', String(plan.planCode || ''));
    params.set('billingCycle', String(plan.billingCycle || ''));
    history.push(`/payment/checkout?${params.toString()}`);
  };

  if (loading) {
    return (
      <GridContent>
        <div style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </GridContent>
    );
  }

  const content = (
    <div style={{ padding: embedded ? 0 : '24px 0' }}>
        <div className={styles.hero}>
          <Title level={3} style={{ textAlign: 'center', marginBottom: 6 }}>
            选择订阅计划
          </Title>
          {currentSubscriptionSummary && (
            <div className={styles.currentSubscriptionSummary}>
              <div className={styles.currentSubscriptionSummaryHeader}>
                <CalendarOutlined />
                <span>当前有效订阅</span>
              </div>
              <div className={styles.currentSubscriptionSummaryTitle}>
                {currentSubscriptionSummary.title}
              </div>
              {currentSubscriptionSummary.description && (
                <div className={styles.currentSubscriptionSummaryDesc}>
                  {currentSubscriptionSummary.description}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            message="订阅计划加载失败"
            description={(error as any)?.message || '请稍后刷新重试，或联系管理员检查支付套餐配置。'}
            style={{ marginBottom: 24 }}
          />
        )}

        {!error && plans.length === 0 && (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="当前暂无可用订阅计划，请联系管理员配置后再试"
            />
          </Card>
        )}

        {!error && plans.length > 0 && (
          <>
            {tabs.length > 1 && (
              <div className={styles.tabToggleWrap}>
                <Segmented
                  size="large"
                  className={styles.tabToggle}
                  value={activeTab?.tabKey}
                  onChange={(v) => setActiveTabKey(String(v))}
                  options={tabs.map((tab) => ({
                    label: tab.tabName || getBillingCycleTabLabel(tab.tabKey),
                    value: tab.tabKey,
                  }))}
                />
              </div>
            )}

            {cyclePlans.length === 0 ? (
              <Card>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`当前暂无${activeTab?.tabName || '该分组'}套餐`}
                />
              </Card>
            ) : (
              <div
                className={styles.plansGrid}
                style={cyclePlans.length === 1 ? { gridTemplateColumns: 'min(520px, 1fr)' } : undefined}
              >
                {cyclePlans.map((plan) => {
                  const purchaseLimit = Number((plan as any).purchaseLimit);
                  const isSelected =
                    String(selectedPlanCode || '').toLowerCase() === String(plan.planCode || '').toLowerCase();
                  const isCurrentVariant = Boolean(plan.current);
                  const actionDisabled = Boolean(plan.actionDisabled);
                  const actionLabel = String(
                    plan.actionLabel ||
                      (isCurrentVariant ? '当前订阅' : formatPriceValue(plan.price) === '免费' ? '立即体验' : '立即订阅'),
                  );

                return (
                  <Card
                    key={`${activeTab?.tabKey || 'tab'}-${plan.id}`}
                    className={`${styles.planCard} ${isSelected ? styles.selectedCard : ''}`}
                    onClick={() => {
                      setSelectedPlanCode(plan.planCode);
                    }}
                    hoverable
                    role="button"
                    aria-pressed={isSelected}
                  >
                    {isSelected && (
                      <div className={styles.selectedBadge}>
                        <CheckCircleFilled />
                        已选中
                      </div>
                    )}
                    {isCurrentVariant && (
                      <div className={styles.currentPlanBadge}>
                        <CheckCircleFilled style={{ marginRight: 4 }} />
                        当前订阅
                      </div>
                    )}

                    <Space direction="vertical" size={0} style={{ width: '100%', padding: '22px 20px 18px 20px' }}>
                      <div className={styles.planHeader}>
                        <Space align="center" size={12}>
                          <div
                            style={{
                              background: isSelected ? token.colorPrimary : token.colorFillTertiary,
                              borderRadius: 12,
                              padding: 8,
                              display: 'flex',
                            }}
                          >
                            <CrownOutlined style={{ fontSize: 22, color: isSelected ? '#fff' : token.colorPrimary }} />
                          </div>
                          <div>
                            <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>
                              {plan.planName}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              {getPlanSubtitle(plan)}
                            </Text>
                          </div>
                        </Space>
                      </div>

                      <div className={styles.priceContainer}>
                        <div className={styles.price}>
                          {formatPriceValue(plan.price) === '免费' ? (
                            <span style={{ fontSize: 34, fontWeight: 900 }}>免费</span>
                          ) : (
                            <>
                              <span className={styles.currency}>¥</span>
                              {formatPriceValue(plan.price)}
                            </>
                          )}
                          <span className={styles.cycle}>/ {getBillingCycleLabel(plan.billingCycle)}</span>
                        </div>
                        {getDisplayBadge(plan) && (
                          <Tag
                            color={normalizeCycleKey(plan.billingCycle) === 'TWO_WEEK' ? 'volcano' : 'green'}
                            style={{ borderRadius: 999, marginTop: 8 }}
                          >
                            {getDisplayBadge(plan)}
                          </Tag>
                        )}
                      </div>

                      {(plan.description || '') && !String(plan.displaySubtitle || '').trim() && (
                        <div style={{ marginBottom: 12, minHeight: 40 }}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {plan.description}
                          </Text>
                        </div>
                      )}

                      <div style={{ flex: 1, marginBottom: 14 }}>
                        {getPlanFeatures(plan).map((feature, index) => (
                          <div className={styles.featureItem} key={`${plan.id}-feature-${index}`}>
                            <CheckCircleFilled className={styles.checkIcon} />
                            <Text>{feature}</Text>
                          </div>
                        ))}
                        {Number.isFinite(purchaseLimit) && purchaseLimit > 0 && (
                          <div className={styles.featureItem}>
                            <InfoCircleFilled style={{ color: token.colorWarning, fontSize: 18, marginTop: 2 }} />
                            <Text type="warning">每人限购 {purchaseLimit} 次</Text>
                          </div>
                        )}
                      </div>

                      {(() => {
                        return (
                          <Button
                            type={isSelected ? 'primary' : 'default'}
                            block
                            className={styles.subscribeButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlanCode(plan.planCode);
                              handleSubscribe(plan);
                            }}
                            disabled={actionDisabled}
                          >
                            {actionLabel}
                          </Button>
                        );
                      })()}
                    </Space>
                  </Card>
                );
                })}
              </div>
            )}

            {selectedPlan && !selectedPlanIsFree && (
              <div className={styles.stickyBar}>
                <div className={styles.stickyBarInner}>
                  <div style={{ minWidth: 0 }}>
                    <div className={styles.stickyTitle}>
                      已选择「{selectedPlan.planName}」{' '}
                      <span style={{ fontWeight: 700, color: token.colorTextSecondary }}>
                        套餐周期 {selectedPlan.durationDays} 天
                      </span>
                    </div>
                    <div className={styles.stickyMeta}>
                      {formatPriceValue(selectedPlan.price) === '免费' ? '免费' : `¥${formatPriceValue(selectedPlan.price)}`}，包含 {formatQuota(selectedPlan.creditQuota)} 点数
                    </div>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => handleSubscribe(selectedPlan)}
                    disabled={Boolean(selectedPlan.actionDisabled)}
                  >
                    {selectedPlan.actionLabel || (formatPriceValue(selectedPlan.price) === '免费' ? '立即体验' : '立即订阅')}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <GridContent>
      {content}
    </GridContent>
  );
};

export default Plans;
